import {AxiosResponse} from "axios";
import React from "react";
import {RouteComponentProps, withRouter} from "react-router";
import {Api} from "../../../../helpers/api";
import {errorMessages} from "../../../../helpers/errorMessage";
import {routingAddresses} from "../../../../helpers/routing";
import {ValidatedValue} from "../../../../helpers/validatedValue";
import Validator from "../../../../helpers/validator";
import i18n from "../../../../i18n/i18n";
import {CompanyModel, NewCompanyDetailsModel} from "../../../models/company";
import {CountryModel} from "../../../../common/models/country";
import {HttpErrorModel} from "../../../../common/models/httpError";
import {CompanyService} from "../../../services/companyService";
import {CountryService} from "../../../../common/services/countryService";
import Button from "../../../../common/components/shared/button/button";
import Checkbox from "../../../../common/components/shared/checkbox/checkbox";
import DropDown from "../../../../common/components/shared/dropDown/dropDown";
import {HttpError} from "../../../../common/components/shared/httpError/httpError";
import TextInput from "../../../../common/components/shared/textInput/textInput";
import {storesContext} from "../../../../common/stores/stores";
import {ICompanyStore} from "../../../stores/companyStore";
import {IDetailInStore} from "../../../stores/detailInStore";
import {observer} from "mobx-react";
import "./companyForm.scss";
import {ReactComponent as RemoveIcon} from "../../../../statics/icons/icon_remove.svg";
import {Popup} from "../../../../common/components/shared/popup/popup";
import Form from "../../../../common/components/shared/form/form";
import {Utils} from "../../../../helpers/utils";
import {DropDownUtils} from "../../../../helpers/dropDownUtils";
import Loader from "../../../../common/components/shared/loader/loader";

interface CompanyFormState {
    countries: CountryModel[];
    isSubmitButtonBlocked: boolean;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    selectedElement: number | undefined;
    displayRemovePopup: boolean;
    countriesResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
}

interface CompanyFormProps extends RouteComponentProps {
    companyData?: CompanyModel | null;
    isUpdate: boolean;
}

class CompanyForm extends React.Component <CompanyFormProps, CompanyFormState> {
    static contextType = storesContext;

    private taxNo!: ValidatedValue<string>;
    private bankTransfer!: ValidatedValue<boolean>;

    constructor(props: any) {
        super(props);

        const companyValidators = [{
            name: "valueIsNotEmpty",
            fn: Validator.isNotEmpty,
            errorMessage: errorMessages.required
        }];

        this.taxNo = new ValidatedValue("", companyValidators);
        this.bankTransfer = new ValidatedValue(false, companyValidators);

        this.state = {
            countries: [],
            isSubmitButtonBlocked: false,
            response: undefined,
            selectedElement: undefined,
            displayRemovePopup: false,
            countriesResponse: undefined
        };
    }

    async componentDidMount() {
        await this.getCountries();
        this.setCompanyData();
    }

    componentWillUnmount() {
        this.context.companyStore.clearStore();
    }

    render() {
        if (this.state.countries.length > 0 || !Api.isSuccess(this.state.countriesResponse)) {
            return (
                <Form
                    class="company-form"
                    renderError={this.renderErrors()}
                    renderContent={this.renderContent()}
                    isSubmitButtonBlocked={this.state.isSubmitButtonBlocked}
                    backAddress={routingAddresses.dictionaries.companies.list}
                    submit={this.submit.bind(this)}
                />
            );
        } else {
            return <Loader/>;
        }
    }

    protected renderErrors() {
        if (!Api.isSuccess(this.state.response)) {
            return (
                <HttpError
                    response={this.state.response}
                    cause={this.getErrorMessage()}
                    translatedFields={{
                        taxNo: i18n.t("dictionaries.companies.httpErrors.taxNo"),
                        name: i18n.t("dictionaries.companies.httpErrors.name"),
                    }}
                    translatedFieldsForEmpty={{
                        taxNo: i18n.t("dictionaries.companies.httpErrors.taxNoForEmpty"),
                        name: i18n.t("dictionaries.companies.httpErrors.nameForEmpty"),
                        street: i18n.t("dictionaries.companies.httpErrors.streetForEmpty"),
                        postalCode: i18n.t("dictionaries.companies.httpErrors.postalCodeForEmpty"),
                        city: i18n.t("dictionaries.companies.httpErrors.cityForEmpty"),
                        countryName: i18n.t("dictionaries.companies.httpErrors.countryNameForEmpty"),
                        phoneNo: i18n.t("dictionaries.companies.httpErrors.phoneNoForEmpty")
                    }}
                    object={i18n.t("dictionaries.companies.httpErrors.object")}
                />
            );
        }
    }

    protected renderContent() {
        const companyStore = this.context.companyStore as ICompanyStore;

        return (
            <>
                <TextInput
                    label={i18n.t("dictionaries.companies.headers.taxNo")}
                    validatedValue={this.taxNo}
                    testId="company-tax-no"/>
                <Checkbox
                    label={i18n.t("dictionaries.companies.bankTransferLabel")}
                    size="medium"
                    validatedValue={this.bankTransfer}
                    testId={"company-bank-transfer"}
                />
                {companyStore.details.map((element, detailId) =>
                    this.renderCompanyDetailsSection(detailId)
                )}
                {this.renderRemovePopup(this.confirmRemove.bind(this))}
                <Button
                    text={i18n.t("dictionaries.companies.actions.addNextDetail")}
                    type="primary"
                    variant="text"
                    className="add-next-detail-btn"
                    testId="add-next-detail"
                    onClick={() => this.addNextDetail()}
                />
            </>
        );
    }

    private renderCompanyDetailsSection(detailId: number) {
        const companyStore = this.context.companyStore as ICompanyStore;
        const addressClassName = companyStore.details[detailId].isEditData ? "company-address new-section" : "company-address disabled";

        return (
            <div className="address-section" key={`company-address-${detailId}`}>
                <div className={addressClassName}>
                    <TextInput
                        disabled={this.props.isUpdate && !companyStore.details[detailId].isEditData}
                        label={i18n.t("dictionaries.companies.headers.name")}
                        validatedValue={companyStore.details[detailId].name} testId="company-name"/>
                    <TextInput
                        disabled={this.props.isUpdate && !companyStore.details[detailId].isEditData}
                        label={i18n.t("dictionaries.companies.headers.street")}
                        validatedValue={companyStore.details[detailId].street} testId="company-street"/>
                    <TextInput
                        disabled={this.props.isUpdate && !companyStore.details[detailId].isEditData}
                        label={i18n.t("dictionaries.companies.headers.postalCode")}
                        validatedValue={companyStore.details[detailId].postalCode} testId="company-postal-code"/>
                    <TextInput
                        disabled={this.props.isUpdate && !companyStore.details[detailId].isEditData}
                        label={i18n.t("dictionaries.companies.headers.city")}
                        validatedValue={companyStore.details[detailId].city} testId="company-city"/>
                    <DropDown
                        disabled={this.props.isUpdate && !companyStore.details[detailId].isEditData}
                        label={i18n.t("dictionaries.companies.headers.countryName")}
                        validatedValue={companyStore.details[detailId].countryImoCode}
                        items={this.countriesToDropDownMap}
                        testId="company-country"
                        error={!Api.isSuccess(this.state.countriesResponse) ? i18n.t("errorMessages.countriesErrorSuffix") : ""}
                        reloadFunc={() => this.getCountries()}
                    />
                    <TextInput
                        disabled={this.props.isUpdate && !companyStore.details[detailId].isEditData}
                        label={i18n.t("dictionaries.companies.headers.phoneNo")}
                        validatedValue={companyStore.details[detailId].phoneNo} testId="company-phone-no"/>
                </div>
                {companyStore.details.length > 1 && companyStore.details[detailId].isEditData && (
                    <Button
                        text={i18n.t("remove")}
                        type="primary"
                        variant="text"
                        className="remove-detail-btn"
                        testId={`remove-${detailId}`}
                        onClick={() => this.setState({selectedElement: detailId, displayRemovePopup: true})}
                    />
                )}
            </div>
        );
    }

    protected renderRemovePopup(confirmRemove: any) {
        const content = <div>
            <p>{i18n.t("dictionaries.companies.actions.removeDetail.line1")}</p>
            <p>{i18n.t("dictionaries.companies.actions.removeDetail.line2")}</p>
        </div>;

        return (
            <Popup
                open={this.state.displayRemovePopup}
                className="remove-popup popup-with-secondary-button-color"
                header={i18n.t("dictionaries.companies.actions.removeDetail.header")}
                secondHeader={i18n.t("dictionaries.companies.actions.removeDetail.secondHeader")}
                icon={<div><RemoveIcon/></div>}
                content={content}
                actionText={i18n.t("dictionaries.companies.actions.removeDetail.actionText")}
                action={confirmRemove}
                cancel={() => this.setState({displayRemovePopup: false})}
                testId="remove-popup"
            />
        );
    }

    private confirmRemove() {
        const companyStore = this.context.companyStore as ICompanyStore;

        if (this.state.selectedElement !== undefined) {
            companyStore.removeDetail(this.state.selectedElement);

            this.setState({selectedElement: undefined, displayRemovePopup: false});
        }
    }

    private addNextDetail() {
        const companyStore = this.context.companyStore as ICompanyStore;
        companyStore.createDetail();
    }

    private getErrorMessage() {
        return this.props.isUpdate
            ? i18n.t("dictionaries.companies.errors.modifyErrorSuffix")
            : i18n.t("dictionaries.companies.errors.addErrorSuffix");
    }

    public get countriesToDropDownMap() {
        return DropDownUtils.listToDropDownMap(this.state.countries, "imoCode");
    }

    public get isValid() {
        const companyStore = this.context.companyStore as ICompanyStore;
        const details = companyStore.details;
        let isValid = true;

        this.taxNo.validate();
        details.map((detail) => {
            if (!this.detailIsValid(detail)) {
                isValid = false;
            }
        });

        return this.taxNo.isValid && isValid;
    }

    public detailIsValid(detail: IDetailInStore) {
        detail.countryImoCode.validate();
        detail.name.validate();
        detail.street.validate();
        detail.postalCode.validate();
        detail.city.validate();
        detail.phoneNo.validate();

        return detail.countryImoCode.isValid && detail.name.isValid
            && detail.street.isValid && detail.postalCode.isValid
            && detail.city.isValid && detail.phoneNo.isValid;
    }

    private setCompanyData() {
        this.props.companyData?.details.map((detail, detailId) => {
            this.context.companyStore.setCompanyStore(detail, this.state.countries, detailId);
        });
        this.taxNo.setValue(this.props.companyData?.taxNo || "");
        this.bankTransfer.setValue(this.props.companyData?.bankTransfer || false);
    }

    private async submit() {
        this.setState({response: undefined});

        if (this.isValid) {
            this.setState({isSubmitButtonBlocked: true});
            await this.addOrModifyCompany();
            this.setState({isSubmitButtonBlocked: false});

            if (Api.isSuccess(this.state.response)) {
                this.props.history.push(routingAddresses.dictionaries.companies.list);
            }
        }
        Utils.scrollToFirstErrorMessage();
    }

    private async addOrModifyCompany() {
        const companyStore = this.context.companyStore as ICompanyStore;

        if (this.props.isUpdate && this.props.companyData?.id) {
            const companyDetails: NewCompanyDetailsModel[] = companyStore.details.map((detail) => {
                return this.prepareCompanyDetail(detail);
            });

            this.setState({
                response: await CompanyService.modifyCompany({
                    taxNo: this.taxNo.value.trim(),
                    bankTransfer: this.bankTransfer.value,
                    details: companyDetails
                }, this.props.companyData?.id)
            });
        } else {
            const companyDetails: NewCompanyDetailsModel[] = [];

            companyStore.details.forEach((detail: IDetailInStore) => {
                companyDetails.push(this.prepareCompanyDetail(detail));
            });

            this.setState({
                response: await CompanyService.addCompany({
                    taxNo: this.taxNo.value.trim(),
                    bankTransfer: this.bankTransfer.value,
                    details: companyDetails
                })
            });
        }
    }

    private prepareCompanyDetail(detail: IDetailInStore): NewCompanyDetailsModel {
        return {
            name: detail.name.value.trim(),
            phoneNo: detail.phoneNo.value.trim(),
            street: detail.street.value.trim(),
            city: detail.city.value.trim(),
            postalCode: detail.postalCode.value.trim(),
            countryImoCode: detail.countryImoCode.value
        };
    }

    private async getCountries() {
        const countriesResponse = await CountryService.getAll();

        this.setState({
            countries: (countriesResponse)?.data?.data || [],
            countriesResponse
        });
    }
}

export default withRouter(observer(CompanyForm));
